import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import {
    dayHasAttendanceMark,
    getEmployeeDayCalendarTone,
    getLogsForCalendarDay
} from "../../../utils/employeeDayEffectiveMinutes"
import {
    buildMonthCalendarDays,
    isCalendarDayFuture,
    isSameCalendarMonthAsToday
} from "../../../utils/monthCalendarGrid"

const CALENDAR_LEGEND_TONES = [
    { tone: "exact", key: "exactExit" },
    { tone: "over", key: "lateExit" },
    { tone: "short", key: "earlyExit" },
    { tone: "absent", key: "absent" }
]

const UserStatsMonthCalendar = ({ isDarkTheme, selectedDay, onSelectDay }) => {
    const { t } = useTranslation("stats")
    const { year, month } = useAppStore((state) => state.userStatsDateFilter)
    const logsByDay = useAppStore((state) => state.userStatsMonthLogsByDay)
    const loading = useAppStore((state) => state.loadingUserStatsMonthLogs)

    const weekdayLabels = t("weekdaysShort", { returnObjects: true })

    const { calendarDays, numberOfRows } = useMemo(
        () => buildMonthCalendarDays(year, month),
        [year, month]
    )

    const gridRows = useMemo(
        () => Array.from({ length: numberOfRows }, () => "minmax(24px, 1fr)").join(" "),
        [numberOfRows]
    )

    const dayMetaByKey = useMemo(() => {
        if (loading || logsByDay == null) return null
        const map = new Map()
        for (const d of calendarDays) {
            if (!d.isCurrentMonth) continue
            const logs = getLogsForCalendarDay(logsByDay, d.year, d.month, d.date)
            map.set(`${d.year}-${d.month}-${d.date}`, {
                tone: getEmployeeDayCalendarTone(logs),
                showAttendanceMark: dayHasAttendanceMark(logs)
            })
        }
        return map
    }, [calendarDays, logsByDay, loading])

    const viewingCurrentMonth = isSameCalendarMonthAsToday(year, month)

    const handleDayCellClick = useCallback(
        (day) => {
            if (!day.isCurrentMonth || !onSelectDay) return
            if (isCalendarDayFuture(day)) return
            onSelectDay({ year: day.year, month: day.month, date: day.date })
        },
        [onSelectDay]
    )

    return (
        <div className="usv-panel usv-panel--calendar">
            <div className="usv-calendar-body asv-employee-calendar-slot">
                <div
                    className={`asv-employee-calendar-grid-wrap cv-grid-container usv-calendar-grid${isDarkTheme ? " asv-employee-calendar-grid-wrap--dark" : ""}`}
                >
                    <div
                        key={`${year}-${month}`}
                        className={`cv-calendar-grid ${isDarkTheme ? "dark-calendar-grid" : ""}`}
                        style={{ gridTemplateRows: `auto ${gridRows}` }}
                        data-viewing-current-month={viewingCurrentMonth || undefined}
                    >
                        {weekdayLabels.map((label) => (
                            <div key={label} className="cv-day-name">
                                {label}
                            </div>
                        ))}
                        {calendarDays.map((day) => {
                            const cellKey = `${day.year}-${day.month}-${day.date}`
                            const isFutureDay = day.isCurrentMonth && isCalendarDayFuture(day)
                            const dayMeta = dayMetaByKey?.get(cellKey)
                            const tone = !isFutureDay ? dayMeta?.tone : null
                            const toneClass = day.isCurrentMonth && tone ? `asv-emp-day--${tone}` : ""
                            const attendanceMarkClass =
                                !isFutureDay && day.isCurrentMonth && dayMeta?.showAttendanceMark
                                    ? "asv-emp-day-cell--attendance-mark"
                                    : ""
                            const darkCellClass = isDarkTheme ? "asv-emp-day-cell--dark" : ""
                            const isSelected =
                                selectedDay != null &&
                                selectedDay.year === day.year &&
                                selectedDay.month === day.month &&
                                selectedDay.date === day.date
                            const selectedClass = isSelected ? "asv-emp-day-cell--selected" : ""
                            const outsideMonthClass = !day.isCurrentMonth ? "asv-emp-day-cell--outside-month" : ""
                            const futureClass = isFutureDay ? "asv-emp-day-cell--future" : ""

                            return (
                                <div
                                    key={cellKey}
                                    className={`cv-day asv-emp-day-cell ${darkCellClass} ${!day.isCurrentMonth ? "cv-day-other-month" : ""} ${outsideMonthClass} ${futureClass} ${toneClass} ${attendanceMarkClass} ${selectedClass}`.trim()}
                                    onClick={() => handleDayCellClick(day)}
                                    aria-disabled={!day.isCurrentMonth || isFutureDay || undefined}
                                >
                                    {day.date}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div
                    className={`asv-emp-calendar-legend usv-calendar-legend ${isDarkTheme ? "asv-emp-calendar-legend--dark" : ""}`}
                    aria-label={t("calendar.legendAria")}
                >
                    {CALENDAR_LEGEND_TONES.map(({ tone, key }) => (
                        <span key={tone} className="asv-emp-calendar-legend-item">
                            <span
                                className={`asv-emp-calendar-legend-swatch asv-emp-calendar-legend-swatch--${tone}`}
                                aria-hidden
                            />
                            <span className="asv-emp-calendar-legend-label">{t(`calendar.${key}`)}</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default UserStatsMonthCalendar
